import { useCallback } from 'react';
import axios from 'axios';
import { User } from '../types';
import { API_ENDPOINTS } from '../constants';

export const useUserActions = (
  showNotification: (message: string, severity: 'success' | 'error' | 'info' | 'warning') => void,
  logAction: (action: string) => void,
  refetchUsers: () => void
) => {
  const toggleActivation = useCallback(async (user: User) => {
    try {
      const response = await axios.post(API_ENDPOINTS.TOGGLE_ACTIVATION, {
        userId: user.id,
        currentStatus: user.deactivation
      });
      
      if (response.data.success) {
        const action = user.deactivation === 0 ? 'деактивировал' : 'активировал';
        logAction(`${action} учетную запись пользователя "${user.username}" | Пользователи`);
        refetchUsers();
      }
    } catch (error) {
      console.error('Ошибка при изменении статуса активации:', error);
      showNotification('Не удалось изменить статус активации', 'error');
    }
  }, [showNotification, logAction, refetchUsers]);

  const togglePOS = useCallback(async (user: User) => {
    try {
      const response = await axios.post(API_ENDPOINTS.TOGGLE_POS, {
        userId: user.id,
        currentStatus: user.pos
      });
      
      if (response.data.success) {
        const action = user.pos === 0 ? 'разблокировал' : 'заблокировал';
        logAction(`${action} кассу пользователя "${user.username}" | Пользователи`);
        refetchUsers();
      }
    } catch (error) {
      console.error('Ошибка при изменении статуса кассы:', error);
      showNotification('Не удалось изменить статус кассы', 'error');
    }
  }, [showNotification, logAction, refetchUsers]);

  const toggleReports = useCallback(async (user: User) => {
    try {
      const response = await axios.post(API_ENDPOINTS.TOGGLE_REPORTS, {
        userId: user.id,
        currentStatus: user.reports
      });
      
      if (response.data.success) {
        const action = user.reports === 0 ? 'разблокировал' : 'заблокировал';
        logAction(`${action} отчеты пользователя "${user.username}" | Пользователи`);
        refetchUsers();
      }
    } catch (error) {
      console.error('Ошибка при изменении статуса отчетов:', error);
      showNotification('Не удалось изменить статус отчетов', 'error');
    }
  }, [showNotification, logAction, refetchUsers]);

  const deleteUser = useCallback(async (user: User) => {
    try {
      await axios.delete(`${API_ENDPOINTS.DELETE_USER}/${user.id}`);
      showNotification(`Пользователь ${user.username} успешно удален`, 'success');
      logAction(`удалил все данные о пользователе "${user.username}" | Пользователи`);
      refetchUsers();
    } catch (error) {
      console.error('Ошибка при удалении пользователя:', error);
      showNotification('Не удалось удалить пользователя', 'error');
    }
  }, [showNotification, logAction, refetchUsers]);

  const massReportsLock = useCallback(async () => {
    try {
      const response = await axios.post(API_ENDPOINTS.MASS_REPORTS_LOCK);
      if (response.data.success) {
        showNotification(`Отчеты успешно заблокированы для ${response.data.affectedRows} пользователей`, 'success');
        logAction('массово заблокировал отчеты для всех пользователей | Пользователи');
        refetchUsers();
      }
    } catch (error) {
      console.error('Ошибка при массовой блокировке отчетов:', error);
      showNotification('Не удалось заблокировать отчеты', 'error');
    }
  }, [showNotification, logAction, refetchUsers]);

  const massPOSLock = useCallback(async () => {
    try {
      const response = await axios.post(API_ENDPOINTS.MASS_POS_LOCK);
      if (response.data.success) {
        showNotification(`Кассы успешно заблокированы для ${response.data.affectedRows} пользователей`, 'success');
        logAction('массово заблокировал кассы для всех пользователей | Пользователи');
        refetchUsers();
      }
    } catch (error) {
      console.error('Ошибка при массовой блокировке касс:', error);
      showNotification('Не удалось заблокировать кассы', 'error');
    }
  }, [showNotification, logAction, refetchUsers]);

  return {
    toggleActivation,
    togglePOS,
    toggleReports,
    deleteUser,
    massReportsLock,
    massPOSLock
  };
};

